import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export interface SavedFilter {
  id: string;
  user_id: string;
  entity_type: string;
  name: string;
  filters: Record<string, any>;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface FilterValue {
  field: string;
  operator: 'eq' | 'neq' | 'contains' | 'in' | 'gt' | 'lt' | 'gte' | 'lte' | 'between';
  value: any;
  logic?: 'AND' | 'OR'; // For multi-select fields like skills
}

export function useSavedFilters(entityType: string) {
  const { user } = useAuth();
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const [activeFilters, setActiveFilters] = useState<FilterValue[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch saved filters for this entity type
  const fetchSavedFilters = useCallback(async () => {
    if (!user) return;

    try {
      const { data: appUser } = await supabase
        .from('app_users')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (!appUser) return;

      const { data, error } = await supabase
        .from('saved_filters')
        .select('*')
        .eq('user_id', appUser.id)
        .eq('entity_type', entityType)
        .order('name');

      if (error) throw error;
      
      // Cast the data properly
      const typedData = (data || []).map(item => ({
        ...item,
        filters: item.filters as Record<string, any>
      })) as SavedFilter[];
      
      setSavedFilters(typedData);

      // Load default filter if exists
      const defaultFilter = typedData.find(f => f.is_default);
      if (defaultFilter) {
        setActiveFilters(defaultFilter.filters.filters || []);
      }
    } catch (error) {
      console.error('Error fetching saved filters:', error);
    }
  }, [user, entityType]);

  useEffect(() => {
    fetchSavedFilters();
  }, [fetchSavedFilters]);

  // Save a new filter
  const saveFilter = async (name: string, filters: FilterValue[], setAsDefault = false) => {
    if (!user) return;

    setLoading(true);
    try {
      const { data: appUser } = await supabase
        .from('app_users')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (!appUser) throw new Error('User not found');

      // If setting as default, unset other defaults
      if (setAsDefault) {
        await supabase
          .from('saved_filters')
          .update({ is_default: false })
          .eq('user_id', appUser.id)
          .eq('entity_type', entityType);
      }

      const { data, error } = await supabase
        .from('saved_filters')
        .insert({
          user_id: appUser.id,
          entity_type: entityType,
          name,
          filters: { filters },
          is_default: setAsDefault
        })
        .select()
        .single();

      if (error) throw error;

      const typedData = {
        ...data,
        filters: data.filters as Record<string, any>
      } as SavedFilter;

      setSavedFilters(prev => [...prev, typedData]);
      toast.success(`Filter "${name}" saved`);
    } catch (error) {
      console.error('Error saving filter:', error);
      toast.error('Failed to save filter');
    } finally {
      setLoading(false);
    }
  };

  // Update an existing filter
  const updateFilter = async (id: string, updates: Partial<Pick<SavedFilter, 'name' | 'filters' | 'is_default'>>) => {
    setLoading(true);
    try {
      if (!user) throw new Error('User not found');

      // If setting as default, unset other defaults
      if (updates.is_default) {
        const { data: appUser } = await supabase
          .from('app_users')
          .select('id')
          .eq('user_id', user.id)
          .single();

        if (appUser) {
          await supabase
            .from('saved_filters')
            .update({ is_default: false })
            .eq('user_id', appUser.id)
            .eq('entity_type', entityType);
        }
      }

      const { error } = await supabase
        .from('saved_filters')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      setSavedFilters(prev => prev.map(f => 
        f.id === id ? { ...f, ...updates } as SavedFilter : f
      ));
      toast.success('Filter updated');
    } catch (error) {
      console.error('Error updating filter:', error);
      toast.error('Failed to update filter');
    } finally {
      setLoading(false);
    }
  };

  // Delete a filter
  const deleteFilter = async (id: string) => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('saved_filters')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setSavedFilters(prev => prev.filter(f => f.id !== id));
      toast.success('Filter deleted');
    } catch (error) {
      console.error('Error deleting filter:', error);
      toast.error('Failed to delete filter');
    } finally {
      setLoading(false);
    }
  };

  // Apply a saved filter
  const applyFilter = (filter: SavedFilter) => {
    setActiveFilters(filter.filters.filters || []);
  };

  // Clear all active filters
  const clearFilters = () => {
    setActiveFilters([]);
  };

  return {
    savedFilters,
    activeFilters,
    setActiveFilters,
    loading,
    saveFilter,
    updateFilter,
    deleteFilter,
    applyFilter,
    clearFilters,
    refetch: fetchSavedFilters
  };
}
